import { ReactNode, createContext, useMemo } from 'react';
import useLoanTypes from '~/hooks/useLoanTypes';
import useLocalStorage from '~/hooks/useLocalStorage';
import { defaultAmountQuantityRange, defaultFromToRange } from './LoanOfficerSearchParams';

export type RealtorSearchParams = {
  fullname: string;
  bre: string;
  team: {
    role: AgentTeamRoles | '';
    size: FromToInterval;
  };
  office: {
    address: {
      state: StateAlias | EmptyString;
      zip: string;
      city: string;
      county: string;
    };
    name: string;
    strict: boolean;
  };
  company: {
    address: {
      state: StateAlias | EmptyString;
      zip: string;
      city: string;
      county: string;
    };
    name: string;
    strict: boolean;
  };
  period: Period;
  sales: {
    transaction_side: {
      sale: ParamInterval;
      buyer: ParamInterval;
      seller: ParamInterval;
      dual: ParamInterval;
    };
    loan_type: {
      sba?: ParamInterval;
      pp?: ParamInterval;
      cnv?: ParamInterval;
      fha?: ParamInterval;
      va?: ParamInterval;
      rev?: ParamInterval;
      other?: ParamInterval;
    };
  };
  transaction_address: {
    state: StateAlias | EmptyString;
    zip: string;
    city: string;
    county: string;
  };
};

export type AgentsSearchParamsContextState = {
  defaultValues: RealtorSearchParams;
  searchParams: RealtorSearchParams;
  setSearchParams: (newParams: RealtorSearchParams) => void;
};

const initialState: AgentsSearchParamsContextState = {
  defaultValues: {} as RealtorSearchParams,
  searchParams: {} as RealtorSearchParams,
  setSearchParams: () => {},
};

const AgentsSearchParamsContext = createContext(initialState);

type AgentsSearchParamsProviderProps = {
  children: ReactNode;
};

function AgentsSearchParamsProvider({ children }: AgentsSearchParamsProviderProps) {
  const { loanTypes } = useLoanTypes();

  const defaultValues: RealtorSearchParams = useMemo(
    () => ({
      fullname: '',
      bre: '',
      team: {
        role: '',
        size: defaultFromToRange,
      },
      company: {
        address: {
          state: '',
          city: '',
          zip: '',
          county: '',
        },
        name: '',
        strict: false,
      },
      office: {
        address: {
          state: '',
          city: '',
          zip: '',
          county: '',
        },
        name: '',
        strict: false,
      },
      sales: {
        transaction_side: {
          sale: defaultAmountQuantityRange,
          buyer: defaultAmountQuantityRange,
          seller: defaultAmountQuantityRange,
          dual: defaultAmountQuantityRange,
        },
        loan_type: Object.fromEntries(loanTypes.map((type) => [type, defaultAmountQuantityRange])),
      },
      period: 14,
      transaction_address: {
        state: '',
        city: '',
        zip: '',
        county: '',
      },
    }),
    [loanTypes]
  );

  const [searchParams, setSearchParams] = useLocalStorage<RealtorSearchParams>(
    'searchParams:agents',
    defaultValues
  );

  return (
    <AgentsSearchParamsContext.Provider value={{ searchParams, setSearchParams, defaultValues }}>
      {children}
    </AgentsSearchParamsContext.Provider>
  );
}

export { AgentsSearchParamsProvider, AgentsSearchParamsContext };
